import type { Config } from 'tailwindcss';
import colors from 'tailwindcss/colors';

export default {
  darkMode: 'class',
  content: [
    './src/**/*.{js,jsx,ts,tsx}',
    // unison-core's status COLORS maps are class-name strings; JIT must scan them
    './node_modules/@witmetrics/unison-core/dist/**/*.{js,mjs}',
  ],
  theme: {
    colors: {
      current: 'currentColor',
      grey: colors.gray,
      slate: colors.slate,
    },
    extend: {
      backgroundImage: {
        'default-gradient': 'url(./../public/images/gradient-1_sm.png)',
        grad: 'linear-gradient(135deg, #1a86c8, #00579a)',
        'grad-dark': 'linear-gradient(135deg, #5fb2e8, #1276b9)',
        'grad-blue': 'linear-gradient(135deg, #5b8bf0, #2f5fd0)',
        'grad-green': 'linear-gradient(135deg, #25c05c, #12903f)',
        'grad-amber': 'linear-gradient(135deg, #f7b23e, #f2a012)',
        'grad-red': 'linear-gradient(135deg, #ff6b5f, #e23b32)',
        'aurora-mesh':
          'radial-gradient(2400px 940px at -2% -2%, rgba(67,149,190,.82), transparent 56%), radial-gradient(1100px 850px at 72% -2%, rgba(91,132,210,.76), transparent 54%), radial-gradient(1200px 960px at 18% 66%, rgba(190,132,220,.74), transparent 56%), radial-gradient(1300px 980px at 86% 104%, rgba(138,180,255,.88), transparent 56%), radial-gradient(980px 800px at 88% 8%, rgba(255,205,221,.62), transparent 55%), linear-gradient(180deg, #eceef5, #e6eaf3)',
        'aurora-mesh-dark':
          'radial-gradient(2400px 940px at -2% -2%, rgba(52,116,150,.32), transparent 56%), radial-gradient(1100px 850px at 72% -2%, rgba(72,104,168,.30), transparent 54%), radial-gradient(1200px 960px at 18% 66%, rgba(152,104,180,.28), transparent 56%), radial-gradient(1300px 980px at 86% 104%, rgba(110,150,248,.30), transparent 56%), radial-gradient(980px 800px at 88% 8%, rgba(210,160,178,.22), transparent 55%), linear-gradient(180deg, #191623, #13111b)',
        'drawer-mesh':
          'linear-gradient(rgba(255,255,255,.62),rgba(255,255,255,.62)),radial-gradient(1150px 860px at -2% -2%,rgba(67,149,190,.85),transparent 56%),radial-gradient(1380px 1020px at 102% -2%,rgba(94,214,214,.25),transparent 58%),radial-gradient(1200px 960px at 18% 66%,rgba(190,132,220,.74),transparent 56%),radial-gradient(1300px 980px at 86% 104%,rgba(138,180,255,.69),transparent 56%),radial-gradient(980px 800px at 56% 30%,rgba(255,205,221,.62),transparent 55%),linear-gradient(rgba(255,255,255,.86),rgba(255,255,255,.86))',
        'drawer-mesh-dark':
          'linear-gradient(rgba(33,29,47,0.82),rgba(33,29,47,0.82)),radial-gradient(1150px 860px at -2% -2%,rgba(52,116,150,.4),transparent 64%),radial-gradient(1100px 820px at 102% -2%,rgba(96,196,216,.3),transparent 54%),radial-gradient(1200px 960px at 18% 66%,rgba(152,104,180,.36),transparent 56%),radial-gradient(1300px 980px at 86% 104%,rgba(110,150,248,.44),transparent 56%),radial-gradient(980px 800px at 56% 30%,rgba(210,160,178,.3),transparent 55%)',
      },
      fontFamily: {
        sans: ['Inter', 'ui-sans-serif', 'system-ui', 'sans-serif'],
        mono: ['"JetBrains Mono"', 'ui-monospace', 'monospace'],
      },
      borderRadius: {
        lg: '0.5rem',
        xl: '0.75rem',
        '2xl': '1rem',
        '3xl': '1.5rem',
      },
      boxShadow: {
        card: '0px 4px 16px hsla(0, 0%, 0%, 0.04)',
        'pop-up': '0px 4px 10px 0px hsla(0,0%,0%,0.15)',
        dialog: '0px 6px 15px 0px hsla(0,0%,0%,0.2)',
        sidebar: '0px 4px 20px 0 hsla(0,0%,0%,10%)',
        header: '0px 1px 8px 0 hsla(0,0%,0%,10%)',
        ring: '0 6px 14px -5px rgba(0,100,170,.42)',
        'ring-lg': '0 8px 16px -6px rgba(0,100,170,.42)',
        'ring-xl': '0 8px 18px -6px rgba(0,100,170,.42)',
        glass:
          '0 32px 84px -28px rgba(70,60,90,.32), 0 2px 10px rgba(70,60,90,.06)',
        'glass-dark':
          '0 36px 90px -28px rgba(0,0,0,.7), 0 2px 10px rgba(0,0,0,.45)',
        'aurora-card':
          '0 1px 2px rgba(40,30,30,.04), 0 14px 34px -16px rgba(60,50,60,.12)',
        'aurora-card-dark':
          '0 1px 2px rgba(0,0,0,.35), 0 16px 40px -18px rgba(0,0,0,.5)',
        'row-hover': '0 12px 26px -16px rgba(0,100,170,.34)',
        'file-hover': '0 18px 38px -14px rgba(44,78,146,.5)',
      },
      transitionProperty: {
        width: 'width',
      },
      colors: {
        transparent: 'transparent',
        white: '#fff',
        black: '#1b2932',
        'site-background': '#fafafb',
        'dialog-backdrop': '#15181C',
        brand: {
          blue: '#002255',
          orange: '#ff9068',
          accent: '#0064aa',
          'accent-dark': '#4ea6e0',
          'accent-blue': '#2e6bff',
          'accent-teal': '#0f9bb0',
          'accent-indigo': '#5a45e0',
        },
        blue: {
          1: '#E1EFF9',
          2: '#10a2fd',
          3: '#357dd2',
          4: '#255fb0',
          ...colors.blue,
        },
        cyan: {
          1: '#DBF5F7',
          2: '#22d3ee',
          3: '#06b6d4',
          4: '#0e7490',
        },
        emerald: {
          1: '#E4F9F0',
          2: '#6ee7b7',
          3: '#34d399',
          4: '#059669',
        },
        green: {
          1: '#b4f3cf',
          2: '#58bd85',
          3: '#4aa37f',
          4: '#3a8474',
        },
        lime: {
          1: '#d9f99d',
          2: '#bef264',
          3: '#a3e635',
          4: '#84cc16',
        },
        navy: {
          1: '#6c8fe0',
          2: '#234aa4',
          3: '#1a377a',
          4: '#0c2254',
        },
        orange: {
          1: '#ffd1b7',
          2: '#f97b19',
          3: '#ff6b36',
          4: '#cc4700',
          ...colors.orange,
        },
        pink: {
          1: '#FCE2E5',
          2: '#fb7195',
          3: '#f43f5e',
          4: '#e11d48',
        },
        purple: {
          1: '#869af4',
          2: '#5370f4',
          3: '#475ece',
          4: '#3449ae',
          ...colors.purple,
        },
        red: {
          1: '#feb3af',
          2: '#f2625a',
          3: '#df2a20',
          4: '#bd180f',
        },
        yellow: {
          1: '#fef08a',
          2: '#fde047',
          3: '#facc15',
          4: '#eab308',
        },
        grey: {
          1: '#fafafa',
          2: '#f3f5f7',
          3: '#e2e6e9',
          4: '#cedfe8',
          5: '#a7c1d2',
          6: '#7e9eb5',
          7: '#496f88',
          8: '#2d4453',
          9: '#121b21',
        },
        taupe: {
          1: '#938b80',
          2: '#5f5a52',
          3: '#1a1510',
        },
        mauve: {
          1: '#f3eff8',
          2: '#bfb6cb',
          3: '#8b8299',
        },
      },
    },
  },
  plugins: [],
} satisfies Config;
